import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth, UserPreferences } from '../contexts/AuthContext';
import { formatDateTimeWithPreferences, DEFAULT_PREFERENCES } from '../utils/dateFormatter';

interface UserPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
  { value: 'UTC', label: 'Coordinated Universal Time (UTC)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
  { value: 'Europe/Paris', label: 'Central European Time (CET)' },
  { value: 'Europe/Berlin', label: 'Central European Time (CET)' },
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST)' },
  { value: 'Asia/Singapore', label: 'Singapore Time (SGT)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (CST)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' },
  { value: 'Pacific/Auckland', label: 'New Zealand Time (NZT)' },
];

const DATE_FORMAT_OPTIONS: { value: UserPreferences['dateFormat']; label: string; example: string }[] = [
  { value: 'MM/dd/yyyy', label: 'MM/DD/YYYY', example: '12/31/2024' },
  { value: 'dd/MM/yyyy', label: 'DD/MM/YYYY', example: '31/12/2024' },
  { value: 'yyyy-MM-dd', label: 'YYYY-MM-DD', example: '2024-12-31' },
  { value: 'dd-MMM-yyyy', label: 'DD-MMM-YYYY', example: '31-Dec-2024' },
];

const TIME_FORMAT_OPTIONS: { value: UserPreferences['timeFormat']; label: string; example: string }[] = [
  { value: '24H', label: '24-hour', example: '14:30' },
  { value: '12H', label: '12-hour', example: '2:30 PM' },
];

export const UserPreferencesModal: React.FC<UserPreferencesModalProps> = ({ isOpen, onClose }) => {
  const { user, updatePreferences } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<UserPreferences>({
    timezone: user?.preferences?.timezone || DEFAULT_PREFERENCES.timezone,
    dateFormat: user?.preferences?.dateFormat || DEFAULT_PREFERENCES.dateFormat,
    timeFormat: user?.preferences?.timeFormat || DEFAULT_PREFERENCES.timeFormat,
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && user?.preferences) {
      setFormData({
        timezone: user.preferences.timezone || DEFAULT_PREFERENCES.timezone,
        dateFormat: user.preferences.dateFormat || DEFAULT_PREFERENCES.dateFormat,
        timeFormat: user.preferences.timeFormat || DEFAULT_PREFERENCES.timeFormat,
      });
      setError(null);
    }
  }, [isOpen, user?.preferences]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      await updatePreferences(formData);
      onClose();
      // Reload the page to reflect the new preferences across all components
      window.location.reload();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save preferences. Please try again.';
      setError(errorMessage);
      console.error('Error saving preferences:', err.response?.data || err);
    } finally {
      setSaving(false);
    }
  };

  // Generate preview with current settings
  const previewDate = new Date();
  const formattedPreview = formatDateTimeWithPreferences(previewDate, formData);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                    Display Preferences
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-5">
                  {/* Timezone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Timezone
                    </label>
                    <select
                      value={formData.timezone}
                      onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                      className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date Format */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date Format
                    </label>
                    <div className="space-y-2">
                      {DATE_FORMAT_OPTIONS.map((format) => (
                        <label
                          key={format.value}
                          className="flex items-center space-x-3 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="dateFormat"
                            value={format.value}
                            checked={formData.dateFormat === format.value}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                dateFormat: e.target.value as UserPreferences['dateFormat'],
                              })
                            }
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="text-sm text-gray-700">
                            {format.label}{' '}
                            <span className="text-gray-400">({format.example})</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Time Format */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Time Format
                    </label>
                    <div className="flex space-x-4">
                      {TIME_FORMAT_OPTIONS.map((format) => (
                        <label
                          key={format.value}
                          className="flex items-center space-x-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="timeFormat"
                            value={format.value}
                            checked={formData.timeFormat === format.value}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                timeFormat: e.target.value as UserPreferences['timeFormat'],
                              })
                            }
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="text-sm text-gray-700">
                            {format.label}{' '}
                            <span className="text-gray-400">({format.example})</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="pt-3 border-t border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Preview
                    </label>
                    <div className="bg-gray-50 rounded-md p-3 text-center">
                      <span className="text-lg font-mono text-gray-800">{formattedPreview}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      Current date and time in your selected format
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default UserPreferencesModal;
